// Third-party CSS must be imported before application styles so that
// overrides in all.scss (e.g. override.scss) take precedence.
// Lightning CSS (used by rolldown-vite) merges duplicate selectors and
// can drop earlier !important declarations, so correct ordering is essential.
import 'vue-toastification/dist/index.css';
import 'vue-diff/dist/index.css';
import '@/assets/scss/all.scss';
import '@condo/ui/build/style.css';
// MUST stay above the router imports: extracts + strips the #_portal_handoff hash at import time,
// before createWebHistory() snapshots window.location (see portal-handoff-boot.ts).
import { portalHandoffToken } from '@/bootstrap/portal-handoff-boot';
import { bootstrapComponents, configureAppInstance, getUserDetails, registerThirdPartyComponents, registerUnleashPlugin } from '@/bootstrap';
import { applyPortalHandoff, isAbsoluteUrl, isAllowedPortalReturnUrl } from '@/bootstrap/portal-handoff';
import { initSentry, setSentryUser } from '@/bootstrap/sentry';
import { initPostHog } from '@/external/posthog.external';
import { router as externalRouter } from '@/external/external.routes';
import { isPublicRoute, router as publicRouter } from '@/public/public.routes';
import { router } from '@/router';
import { isAuthenticatedCockpitConfig } from '@/runtime-config';
import { authManager } from '@/shared/auth-manager';
import { store } from '@/store';
import type { ICockpitConfig, IPublicCockpitConfig } from '@/types';
import { createGtm } from '@gtm-support/vue-gtm';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createHead } from '@unhead/vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utcPlugin from 'dayjs/plugin/utc';
import { getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { NMessageProvider } from 'naive-ui';
import { createApp, defineAsyncComponent } from 'vue';
import Toast from 'vue-toastification';
import { registerAgGridExtensions } from '@/bootstrap/ag-grid.init';
import { consoleError } from '@/bootstrap/error-wrapper';
import type { IUser } from '@condo/domain';
import VueDiff from 'vue-diff';
import { initHttpClients, initPublicHttpClients, initTypesenseClient } from './api/api-client';
import { initI18n } from './i18n/init-i18n';

dayjs.extend(utcPlugin);
dayjs.extend(advancedFormat);
consoleError();

const loadMainApp = async (user: IUser, configObj: ICockpitConfig, isProduction: boolean) => {
    if (location.search.includes('cache-bust')) {
        history.replaceState({}, document.title, '/');
    }

    const MainApp = defineAsyncComponent(() => import('@/root-containers/MainApp.vue'));
    const head = createHead();
    const app = createApp({
        template: '<MainApp/>',
        components: { MainApp },
        provide: {},
        setup() {},
    });

    /* Inits sentry error tracking */
    initSentry(configObj, app, 'main');
    initI18n(app);

    app.use(router);
    app.use(store);
    app.use(head);

    app.use(VueDiff);

    app.use(Toast, { shareAppContext: true });
    /* vue-query */
    app.use(VueQueryPlugin, {
        queryClientConfig: {
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                    retry: 0,
                },
            },
        },
    });

    /*Register and setup UI components*/
    bootstrapComponents(app);
    /*Third party libraries here*/
    registerThirdPartyComponents(app, configObj);
    /*Any extension for main app instance goes here*/
    configureAppInstance(app);
    registerUnleashPlugin(app, configObj);

    if (configObj.GTM_ENABLED && configObj.GTM_ID?.length) {
        app.use(
            createGtm({
                id: configObj.GTM_ID,
                defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
                compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
                enabled: !!configObj.GTM_ENABLED, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
                debug: !isProduction, // Whether or not display console logs debugs (optional)
                loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
                ignoredViews: [], // Don't trigger events for specified router names (optional)
                trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
            }),
        );
    }

    const cockpitReturnRoute = localStorage.getItem('__COCKPIT_RETURN_ROUTE__');
    const isImpersonating = !!localStorage.getItem('__IMPERSONATED_USER_ID__');
    if (cockpitReturnRoute && !isImpersonating) {
        localStorage.removeItem('__COCKPIT_RETURN_ROUTE__');
        if (isAbsoluteUrl(cockpitReturnRoute)) {
            // Absolute return routes are stored only by the RAM → portal handoff; leave Cockpit entirely.
            if (isAllowedPortalReturnUrl(cockpitReturnRoute, configObj.S_RAM_URL)) {
                location.replace(cockpitReturnRoute);
            }
        } else {
            router.replace(cockpitReturnRoute);
        }
    } else {
        const initialRouteIsMFH = localStorage.getItem('CCS_INITIAL_ROUTE');
        const nextRoute = initialRouteIsMFH ?? configObj.DEFAULT_ROUTE;
        if (location.pathname === '/' && nextRoute?.length) {
            router.replace(nextRoute);
        }
    }

    return app;
};

const loadExternalApp = (configObj: ICockpitConfig, isProduction: boolean) => {
    if (location.search.includes('cache-bust')) {
        history.replaceState({}, document.title, '/');
    }
    const ExternalApp = defineAsyncComponent(() => import('@/external/ExternalApp.vue'));
    const head = createHead();
    const app = createApp({
        template: '<ExternalApp/>',
        components: { ExternalApp },
        provide: {},
        setup() {},
    });

    initSentry(configObj, app, 'external');
    initI18n(app);
    app.use(externalRouter);
    app.use(store);
    app.use(head);

    app.use(Toast, { shareAppContext: true });
    /* vue-query */
    app.use(VueQueryPlugin, {
        queryClientConfig: {
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                    retry: 0,
                },
            },
        },
    });

    /*Register and setup UI components*/
    bootstrapComponents(app);
    /*Third party libraries here*/
    registerThirdPartyComponents(app, configObj);
    /*Any extension for main app instance goes here*/
    configureAppInstance(app);
    registerUnleashPlugin(app, configObj);

    if (configObj.POSTHOG_KEY?.trim() && configObj.POSTHOG_HOST?.trim()) {
        initPostHog(configObj);
    }

    const partnerViewRedirect = localStorage.getItem('__PARTNER_VIEW_REDIRECT__');
    if (partnerViewRedirect) {
        console.log('Redirecting to partner view path:', partnerViewRedirect);
        localStorage.removeItem('__PARTNER_VIEW_REDIRECT__');
        externalRouter.replace(partnerViewRedirect);
    }

    if (configObj.GTM_ENABLED && configObj.GTM_ID?.length) {
        app.use(
            createGtm({
                id: configObj.GTM_ID,
                defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
                compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
                enabled: !!configObj.GTM_ENABLED, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
                debug: !isProduction, // Whether or not display console logs debugs (optional)
                loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
                ignoredViews: [], // Don't trigger events for specified router names (optional)
                trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
            }),
        );
    }

    return app;
};

const loadPublicApp = (configObj: IPublicCockpitConfig, isProduction: boolean) => {
    if (location.search.includes('cache-bust')) {
        history.replaceState({}, document.title, '/');
    }

    const PublicApp = defineAsyncComponent(() => import('@/public/PublicApp.vue'));
    const head = createHead();
    const app = createApp({
        template: '<PublicApp/>',
        components: { PublicApp },
        provide: {},
        setup() {},
    });

    // Set config in store BEFORE anything else
    store.commit('generalData/setConfig', configObj);

    initSentry(configObj, app, 'public');
    initI18n(app);
    app.use(publicRouter);
    app.use(store);
    app.use(head);

    app.use(Toast, { shareAppContext: true });
    app.use(VueQueryPlugin, {
        queryClientConfig: {
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                    retry: 0,
                },
            },
        },
    });

    bootstrapComponents(app);
    configureAppInstance(app);
    registerUnleashPlugin(app, configObj);

    return app;
};

const loadChangePasswordApp = (configObj: ICockpitConfig) => {
    const ChangePasswordApp = defineAsyncComponent(() => import('@/external/views/ChangePassword.vue'));
    const app = createApp({
        template: '<n-message-provider placement="bottom-right"><ChangePasswordApp /></n-message-provider>',
        components: { ChangePasswordApp, NMessageProvider },
    });
    initSentry(configObj, app, 'change-password');
    initI18n(app);
    app.use(store);
    bootstrapComponents(app);
    configureAppInstance(app);
    return app;
};

const loadLoginApp = (configObj: IPublicCockpitConfig) => {
    const LoginApp = defineAsyncComponent(() => import('@/root-containers/Login.vue'));
    const app = createApp({
        template: '<LoginApp :config="config"/>',
        data() {
            return { config: configObj };
        },
        components: {
            LoginApp,
        },
    });
    initSentry(configObj, app, 'login');
    initI18n(app);
    app.use(store);
    bootstrapComponents(app);

    return app;
};

const fetchAppConfig = async (authed: boolean): Promise<IPublicCockpitConfig> => {
    if (!authed) {
        const response = await fetch('/config');
        return response.json();
    }

    const headers = new Headers();

    try {
        const token = await authManager.getToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
    } catch {
        // Session-cookie auth does not have a Firebase token to forward.
    }

    const response = await fetch('/config/authed', {
        credentials: 'include',
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch authenticated config (${response.status})`);
    }

    return response.json();
};

(async () => {
    registerAgGridExtensions();
    const isProduction = (import.meta as Record<string, any>).env.MODE === 'production';
    const publicConfig = await fetchAppConfig(false);

    const isPublicExternalRoute = isPublicRoute(location.pathname);
    // base-check against latest cockpit version, does a full refresh when it changes
    const prevVersion = localStorage.getItem('__VERSION__');
    if (isProduction && prevVersion && publicConfig.HOUSEHOLD_VERSION !== prevVersion) {
        location.reload();
    }

    localStorage.setItem('__VERSION__', publicConfig.HOUSEHOLD_VERSION);

    initializeApp({ apiKey: publicConfig.FBASE_API_KEY, authDomain: publicConfig.FBASE_AUTH_DOMAIN, projectId: publicConfig.FBASE_PROJECT_ID });

    const firebaseUser: any = await authManager.getUserData();

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    store.commit('generalData/setConfig', publicConfig);
    store.commit('ui/setSystemVersion', publicConfig.HOUSEHOLD_VERSION);
    store.commit('ui/setEnvironment', publicConfig.ENVIRONMENT_NAME);
    store.commit('generalData/setIsMobile', isMobile);

    initPublicHttpClients(store);
    if (isPublicExternalRoute && !firebaseUser) {
        loadPublicApp(publicConfig, isProduction).mount('#app');
        return;
    }

    if (!firebaseUser) {
        loadLoginApp(publicConfig).mount('#app');
        return;
    }

    const authenticatedConfig = await fetchAppConfig(true);
    if (!isAuthenticatedCockpitConfig(authenticatedConfig)) {
        throw new Error('Authenticated config response is incomplete');
    }

    store.commit('generalData/setConfig', authenticatedConfig);
    initHttpClients(store);

    store.commit('setUser', firebaseUser);

    // Consume the handoff token before the effective user is resolved so the boot below already
    // sees the impersonated partner preview account and loads the external portal.
    if (portalHandoffToken) {
        await applyPortalHandoff(store, authenticatedConfig, portalHandoffToken);
    }

    const currentUser = getAuth().currentUser;
    const tokenResult = await currentUser?.getIdTokenResult(true);
    if (tokenResult?.claims?.mustChangePassword) {
        loadChangePasswordApp(authenticatedConfig).mount('#app');
        return;
    }

    const [ccsUser] = await Promise.all([getUserDetails(store), store.dispatch('dictionaries/fetchBootstrapData')]);

    setSentryUser(ccsUser);

    // need to be initialized after user is set
    initTypesenseClient(store);

    if (store.getters['isExternal']) {
        loadExternalApp(authenticatedConfig, isProduction).mount('#app');
        return;
    }

    await loadMainApp(ccsUser, authenticatedConfig, isProduction).then(app => {
        app.mount('#app');
    });
})();
